import {
  Box,
  Stack,
  Container,
  Typography,
  Button,
  alpha,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PostHooks from "../../Api/PostHooks";
import { login } from "../../Redux/authSlice";
import AlertComponent from "../../Components/AlertComponent";
import AuthPage from "../../Components/AuthPage";
import ActionDialog from "../../Components/ActionDialog";
import { CheckCircle, Error } from "@mui/icons-material";

const AccountsPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [user, setUser] = useState(location.state.userCredentials);
  const [accounts] = useState(location.state.userAccounts);
  const [account, setAccount] = useState(null);
  const autoLoginInitiated = useRef(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [dialog, setDialog] = useState({
    open: false,
    type: null,
    label: null,
  });

  const openDialog = (type, label) => setDialog({ open: true, type, label });
  const closeDialog = () => setDialog({ open: false, type: null, label: null });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const [loading, response, request] = PostHooks();

  const logInUser = useCallback(
    async (acc) => {
      if (loading) return;
      setAccount(acc);

      try {
        let formData = {
          ...user,
          msisdn: acc?.msisdn,
        };

        const loginResp = await request({
          url: "/login",
          body: formData,
        });

        if (
          loginResp &&
          loginResp.status === 200 &&
          loginResp.data.StatusCode === 0
        ) {
          setSnackbar({
            open: true,
            severity: "success",
            message: "Success, enter OTP...",
          });

          setTimeout(() => {
            navigate("/otp", { state: { user: acc, otpData: loginResp.data } });
          }, 500);
        } else if (
          loginResp &&
          loginResp.status === 200 &&
          loginResp.data.StatusCode === 2
        ) {
          setSnackbar({
            open: true,
            severity: "error",
            message: loginResp.data.StatusMessage,
          });
          setTimeout(() => navigate("/login"), 500);
        } else if (
          loginResp &&
          loginResp.status === 200 &&
          loginResp.data.StatusCode === 3
        ) {
          dispatch(login(loginResp.data));
          setUser((prevUser) => ({ ...prevUser, id: loginResp.data.Entity }));
          setSnackbar({
            open: true,
            severity: "error",
            message: loginResp.data.StatusMessage,
          });
          openDialog("add_kin", "Add next of kin/beneficiary form");
        } else if (
          loginResp &&
          loginResp.status === 200 &&
          loginResp.data.StatusCode === 6
        ) {
          setSnackbar({
            open: true,
            severity: "error",
            message: loginResp.data.StatusMessage,
          });
          setTimeout(() => navigate("/login"), 500);
        } else {
          const errorMessage =
            loginResp?.data?.StatusMessage ??
            loginResp?.data?.message ??
            "An unexpected error occurred. Please try again.";

          setSnackbar({
            open: true,
            severity: "error",
            message: errorMessage,
          });
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.StatusMessage ??
          error.message ??
          "An unexpected error occurred.";
        setSnackbar({
          open: true,
          severity: "error",
          message: errorMessage,
        });
      }
    },
    [dispatch, loading, navigate, request, user]
  );

  useEffect(() => {
    if (accounts.length === 1 && !autoLoginInitiated.current) {
      autoLoginInitiated.current = true;
      logInUser(accounts[0]);
    }
  }, [accounts, logInUser]);

  return (
    <AuthPage title="Login">
      <AlertComponent
        open={snackbar.open}
        handleSnackbar={handleSnackbarClose}
        severity={{ severity: snackbar.severity, message: snackbar.message }}
      />

      <ActionDialog
        open={dialog.open}
        onClose={closeDialog}
        dialogType={dialog.type}
        dialogLabel={dialog.label}
        user={user}
        onSuccess={() => logInUser(account)}
      />

      <Container
        maxWidth="sm"
        sx={{
          height: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Stack direction="column" alignItems="center" spacing={6}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" align="center" gutterBottom>
              Welcome back, sign in.
            </Typography>
            <Typography sx={{ color: "text.secondary" }} align="center">
              Select account to log in.
            </Typography>
          </Box>
        </Stack>

        <Stack
          sx={{
            mt: "20px",
          }}
        >
          {accounts.map((el, i) => (
            <Box
              key={i}
              sx={[
                {
                  border: "1px solid black",
                  margin: "6px",
                  padding: "20px",
                  display: "flex",
                  justifyContent: "space-between",
                  cursor: "pointer",
                },
                {
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                },
              ]}
              onClick={() => logInUser(el)}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignContent: "end",
                  justifyContent: "end",
                }}
              >
                {el.msisdn} <br />
                {el.role}
              </Box>

              {loading && account?.msisdn === el.msisdn ? (
                <CircularProgress size={20} />
              ) : response &&
                response.status === 200 &&
                response.data.StatusCode === 0 &&
                account?.msisdn === el.msisdn ? (
                <CheckCircle color="success" />
              ) : response &&
                response.status === 200 &&
                response.data.StatusCode !== 0 &&
                account?.msisdn === el.msisdn ? (
                <Error color="error" />
              ) : null}
            </Box>
          ))}
        </Stack>

        <Button
          sx={{ mt: "20px" }}
          fullWidth
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Container>
    </AuthPage>
  );
};

export default AccountsPage;
